import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import type { NotificationTone } from '@/types/status'

interface NotificationInput {
  title: string
  message: string
  tone?: NotificationTone
  durationMs?: number
}

interface Notification extends NotificationInput {
  id: string
  tone: NotificationTone
}

interface NotificationContextValue {
  notifications: Notification[]
  notify: (notification: NotificationInput) => string
  dismiss: (id: string) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

function createNotificationId() {
  return globalThis.crypto?.randomUUID?.() ?? `notification-${Date.now()}`
}

export function NotificationProvider({ children }: PropsWithChildren) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const timers = useRef(new Map<string, number>())

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id)
    if (timer) {
      window.clearTimeout(timer)
      timers.current.delete(id)
    }
    setNotifications((current) => current.filter((notification) => notification.id !== id))
  }, [])

  const notify = useCallback(
    ({ tone = 'info', durationMs = 5000, ...input }: NotificationInput) => {
      const id = createNotificationId()
      setNotifications((current) => [...current, { ...input, tone, id }])
      const timer = window.setTimeout(() => dismiss(id), durationMs)
      timers.current.set(id, timer)
      return id
    },
    [dismiss],
  )

  useEffect(
    () => () => {
      timers.current.forEach((timer) => window.clearTimeout(timer))
      timers.current.clear()
    },
    [],
  )

  const value = useMemo(
    () => ({ notifications, notify, dismiss }),
    [dismiss, notifications, notify],
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-relevant="additions">
        {notifications.map((notification) => (
          <article
            className={`toast toast--${notification.tone}`}
            key={notification.id}
            role={notification.tone === 'error' ? 'alert' : 'status'}
          >
            <div>
              <strong>{notification.title}</strong>
              <p>{notification.message}</p>
            </div>
            <button
              className="icon-button"
              type="button"
              onClick={() => dismiss(notification.id)}
              aria-label="Dispensar notificação"
            >
              ×
            </button>
          </article>
        ))}
      </div>
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de NotificationProvider.')
  }
  return context
}
